import { useState } from "react";
import { Link } from "react-router";
import { DailyBars, RangePicker, ShareBars } from "../components/charts";
import { Icon } from "../components/icons";
import {
  Alert,
  Card,
  CardHeader,
  EmptyState,
  LoadingBlock,
  PageHeader,
  Table,
  Td,
  Th,
  Tag,
  buttonStyles,
  cx,
} from "../components/ui";
import { useAuth } from "../auth/AuthProvider";
import { RecordRefundModal } from "../components/RecordRefundModal";
import { SettlementQueueCard } from "../components/SettlementQueueCard";
import { formatNumber, formatRelative } from "../lib/format";
import { chargedMoney, money, moneyCompact, rate } from "../lib/money";
import { useBillingOverview, useCurrencies, useRevenueSeries } from "../lib/queries";
import type { ParkedPayment } from "../lib/types";

/**
 * Platform finance.
 *
 * The page is arranged around one distinction: outstanding credit is money
 * customers have paid and not yet spent, and it is a **liability** — compute
 * still owed to them. Usage is what has actually been earned. A dashboard that
 * adds the two together tells its owner the business is roughly twice its real
 * size, which is the single most expensive mistake a prepaid model invites.
 */
/**
 * What to call a processor in a list an operator reads.
 *
 * Falls back to the stored string rather than hiding an id it does not
 * recognise: a payment taken by a processor this build has since dropped still
 * has to be reconcilable, and a blank cell is worse than a raw name.
 */
function providerLabel(provider: string): string {
  return ({ polar: "Polar", paypal: "PayPal", borderpay: "BorderPay" } as Record<string, string>)[provider] ?? provider;
}

/**
 * The other side of a payment taken in another currency, as the processor's
 * statement shows it: "Rp165.000 · DANA". Whole units and the local grouping,
 * because that is the figure to find on the statement.
 */
export function chargedLabel(charged: { amount: number; currency: string; method: string | null }): string {
  const amount = chargedMoney(charged.amount, charged.currency);
  const [kind, code] = (charged.method ?? "").split(":");
  const method = kind === "qris" ? "QRIS" : kind === "va" ? `${code ?? ""} VA`.trim() : code === "SHOPEE" ? "ShopeePay" : code || null;
  return method ? `${amount} · ${method}` : amount;
}

export function RevenuePage() {
  const [days, setDays] = useState(30);
  const { role } = useAuth();
  const canWrite = role === "superadmin";
  const [refunding, setRefunding] = useState<ParkedPayment | null>(null);
  const { data, isPending, error } = useBillingOverview();
  const series = useRevenueSeries(days);

  if (isPending) return <LoadingBlock />;
  if (error) return <Alert>{error.message}</Alert>;

  const c = data.currency;
  const mtd = data.monthToDate;

  return (
    <>
      <PageHeader
        title="Revenue"
        description="What was paid in, what was earned, and who is running low."
      />

      <section className="grid gap-px overflow-hidden rounded-lg border border-line bg-line sm:grid-cols-2 lg:grid-cols-4">
        {/*
          The caption changes with the free trial, and it has to.

          "Compute you owe" stays true of every dollar here — a granted one buys
          the same containers a paid one does. What stopped being true is that
          all of it was bought, so the note says how much of it was not. Upper
          bound rather than a split: the balance is one number and nothing
          records which dollar a charge consumed.
        */}
        <Figure
          label="Outstanding credit"
          value={moneyCompact(data.liability.outstandingCreditMicros, c)}
          detail={`held by ${formatNumber(data.liability.organizationsWithCredit)} organizations`}
          note={
            data.liability.grantedOutstandingMicros > 0
              ? `A liability — compute you owe. Up to ${money(data.liability.grantedOutstandingMicros, c)} of it was granted, not paid.`
              : "A liability — compute you owe."
          }
          tone="neutral"
        />
        {/*
          The promotional share belongs on this tile rather than beside it.

          Earned counts compute delivered whoever funded it, which is right in
          accrual terms and misleading next to Collected: a reader seeing earned
          above collected infers a collection problem, when the gap can be a
          giveaway being consumed. Naming it here makes the two comparable
          without moving either figure.
        */}
        <Figure
          label="Earned this month"
          value={moneyCompact(mtd.usageMicros, c)}
          detail={
            mtd.promoFundedMicros > 0
              ? `${moneyCompact(mtd.usageMicros - mtd.promoFundedMicros, c)} customer-funded`
              : "used since the 1st"
          }
          note={
            mtd.promoFundedMicros > 0
              ? `Includes ${moneyCompact(mtd.promoFundedMicros, c)} paid for with promotional credit.`
              : "This is the revenue figure."
          }
          tone="good"
        />
        <Figure
          label="Collected this month"
          value={moneyCompact(mtd.topUpsMicros, c)}
          detail={`${moneyCompact(data.topUpsLast30dMicros, c)} in 30 days`}
          note="Cash in, not revenue."
          tone="neutral"
        />
        <Figure
          label="Run rate"
          value={`${moneyCompact(data.runRate.perMonthMicros, c)}/mo`}
          detail={`${formatNumber(data.runRate.billableApps)} apps · ${formatNumber(data.runRate.billableDatabases)} databases · ${formatNumber(data.runRate.billableSchedules)} jobs · ${formatNumber(data.runRate.billableTunnels)} tunnels`}
          note={`${rate(data.runRate.perHourMicros, c)} per hour, plus measured inference.`}
          tone="neutral"
        />
      </section>

      {/*
        Which of the three products is carrying the business, looking back and
        looking at right now.

        Both are compositions of a figure already in the row above, so neither
        introduces a number a reader could add to the total by mistake — that is
        why each says what it is a share *of* rather than presenting three
        free-standing amounts.

        The two answer genuinely different questions and are worth having side
        by side: month-to-date is what has been earned and cannot change, while
        the run rate is what the current fleet earns if nothing is created or
        deleted. Stopping a workload no longer takes it out: it is still billed.
        A product can dominate one and not the other, and that gap is the
        interesting thing on this card.
      */}
      <Card>
        <CardHeader
          title="By product"
          description="What each product earns."
        />
        <div className="grid gap-px bg-line sm:grid-cols-2">
          <div className="bg-surface px-5 py-4">
            <p className="micro">Earned this month</p>
            <p className="mt-1 mb-4 text-xs text-fg-subtle">
              Part of {moneyCompact(mtd.usageMicros, c)}, not on top of it.
            </p>
            <ShareBars
              total={mtd.usageMicros}
              shares={[
                { label: "Apps", value: mtd.byProduct.appMicros },
                { label: "Databases", value: mtd.byProduct.databaseMicros },
                { label: "Scheduled jobs", value: mtd.byProduct.scheduleMicros },
                { label: "Tunnels", value: mtd.byProduct.tunnelMicros },
                { label: "Inference", value: mtd.byProduct.inferenceMicros },
                { label: "Domains", value: mtd.byProduct.domainMicros ?? 0 },
              ]}
              format={(value) => money(value, c)}
              emptyLabel="Nothing earned yet this month"
            />
          </div>
          <div className="bg-surface px-5 py-4">
            <p className="micro">Run rate</p>
            <p className="mt-1 mb-4 text-xs text-fg-subtle">
              What {rate(data.runRate.perHourMicros, c)} an hour is made of.
            </p>
            <ShareBars
              total={data.runRate.perHourMicros}
              shares={[
                { label: "Apps", value: data.runRate.byProduct.appMicros },
                { label: "Databases", value: data.runRate.byProduct.databaseMicros },
                { label: "Scheduled jobs", value: data.runRate.byProduct.scheduleMicros },
                { label: "Tunnels", value: data.runRate.byProduct.tunnelMicros },
                {
                  label: "Inference",
                  value: data.runRate.byProduct.inferenceMicros,
                  /* Named because it is not the same kind of number as the
                     three above it. Apps and databases are on plans that state a
                     rate in advance and scheduled jobs on a flat add-on price;
                     tokens are only knowable from what has already been spent,
                     so this share is a trailing average and will lag a real
                     change in usage by days. */
                  note: "An average of recent use, not a fixed rate.",
                },
              ]}
              format={(value) => rate(value, c)}
              emptyLabel="Nothing is currently running"
            />
          </div>
        </div>
      </Card>

      {/*
        The daily line the tiles above compress into one figure each. Earned
        and collected sit side by side and are never stacked into one total —
        one is revenue, the other is cash, and the whole page's correctness is
        the distinction. Composition is named in the readout rather than
        coloured, as everywhere else.
      */}
      <Card>
        <CardHeader
          title="Daily"
          description="Earned against collected, day by day."
          action={
            /* Rendered only once the ranges exist: an empty picker while the
               query loads reads as a broken control, not a loading one. */
            series.data && (
              <RangePicker ranges={series.data.ranges} value={days} onChange={setDays} />
            )
          }
        />
        {series.isPending ? (
          <LoadingBlock />
        ) : series.error ? (
          <div className="px-4 pb-4">
            <Alert>{series.error.message}</Alert>
          </div>
        ) : (
          <div
            className={cx(
              "grid gap-px bg-line sm:grid-cols-2",
              // Same reasoning as the AI usage tiles: a figure from the
              // previous range that still looks live is worse than a gap.
              series.isFetching && "opacity-50 transition-opacity duration-200",
            )}
          >
            <div className="bg-surface px-4 py-4">
              <p className="micro">Earned</p>
              <div className="mt-3">
                <DailyBars
                  points={series.data.days.map((day) => ({
                    day: day.day,
                    value: day.earnedMicros,
                    parts: [
                      { label: "apps", value: day.appMicros },
                      { label: "databases", value: day.databaseMicros },
                      { label: "scheduled jobs", value: day.scheduleMicros },
                      { label: "tunnels", value: day.tunnelMicros },
                      { label: "inference", value: day.inferenceMicros },
                      { label: "domains", value: day.domainMicros ?? 0 },
                    ],
                  }))}
                  format={(value) => money(value, series.data.currency)}
                  label="Daily revenue earned"
                  windowLabel={`over the last ${days} days`}
                />
              </div>
            </div>
            <div className="bg-surface px-4 py-4">
              <p className="micro">Collected</p>
              <div className="mt-3">
                <DailyBars
                  points={series.data.days.map((day) => ({
                    day: day.day,
                    value: day.collectedMicros,
                  }))}
                  format={(value) => money(value, series.data.currency)}
                  label="Daily top-ups collected"
                  windowLabel={`over the last ${days} days`}
                />
              </div>
            </div>
          </div>
        )}
      </Card>

      {data.liability.uncollectableMicros > 0 && (
        <Alert tone="warning">
          <span className="font-medium">
            {money(data.liability.uncollectableMicros, c)} owed and unlikely to be paid.
          </span>{" "}
          These tenants dipped below zero before their apps stopped. Work already done. Not counted
          in Outstanding credit.
        </Alert>
      )}

      {mtd.adjustmentsMicros !== 0 && (
        <Alert tone="info">
          {money(Math.abs(mtd.adjustmentsMicros), c)} of manual{" "}
          {mtd.adjustmentsMicros > 0 ? "credits" : "debits"} this month. Each one is named in the
          audit log.
        </Alert>
      )}

      <Card>
        {/*
          Soonest first, and "soonest" includes already. A tenant at or below
          zero that the hourly run has not stopped yet sits at the top: it is
          what the overview's "out of credit" concern counts, and this is the
          page that concern links to. The email goes only to the solvent ones —
          there is a different message for having run out — so the list is
          wider than the mail by exactly those rows.
        */}
        <CardHeader
          title="Running out soon"
          description="Less than 72 hours of credit left, or none. Those at zero are stopped on the next run."
        />
        {data.atRisk.length === 0 ? (
          <EmptyState icon="plans" title="Nobody is close to running out" />
        ) : (
          <Table
            head={
              <tr>
                <Th>Organization</Th>
                <Th numeric>Balance</Th>
                <Th numeric priority="md">Burn</Th>
                <Th />
              </tr>
            }
          >
            {data.atRisk.map((org) => (
              <tr key={org.id} className="hover:bg-raised">
                <Td>
                  <Link
                    to={`/organizations/${org.id}`}
                    className="font-medium text-accent hover:underline"
                  >
                    {org.name}
                  </Link>
                </Td>
                <Td numeric className={cx(org.balanceMicros <= 0 && "text-danger")}>
                  {money(org.balanceMicros, c)}
                </Td>
                <Td numeric className="text-fg-subtle" priority="md">
                  {rate(org.burnPerHourMicros, c)}/h
                </Td>
                <Td className="text-right">
                  <Link
                    to={`/organizations/${org.id}`}
                    className="text-sm text-accent hover:underline"
                  >
                    Adjust
                  </Link>
                </Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      <Card>
        <CardHeader
          title="Stopped for non-payment"
          description="Apps stopped, organization read-only. A top-up restores it; after 14 days a deletion date is set, and 7 days later the workloads go. Staff-owned organizations are never deleted."
        />
        {data.suspendedForCredit.length === 0 ? (
          <EmptyState icon="orgs" title="Nobody is suspended for credit" />
        ) : (
          <Table
            head={
              <tr>
                <Th>Organization</Th>
                <Th numeric>Balance</Th>
                <Th priority="sm">Stopped</Th>
                <Th priority="sm">Deletion</Th>
                <Th />
              </tr>
            }
          >
            {data.suspendedForCredit.map((org) => (
              <tr key={org.id} className="hover:bg-raised">
                <Td>
                  <Link
                    to={`/organizations/${org.id}`}
                    className="font-medium text-accent hover:underline"
                  >
                    {org.name}
                  </Link>
                </Td>
                <Td numeric className={cx(org.balanceMicros < 0 && "text-danger")}>
                  {money(org.balanceMicros, c)}
                </Td>
                <Td className="text-fg-subtle" priority="sm">
                  {formatRelative(org.suspendedAt)}
                </Td>
                <Td priority="sm">
                  {org.staffOwned ? (
                    <Tag tone="neutral">Staff — exempt</Tag>
                  ) : org.creditTerminatedAt ? (
                    <span className="text-fg-subtle">Deleted {formatRelative(org.creditTerminatedAt)}</span>
                  ) : org.creditTerminatesAt ? (
                    <span className="text-danger">{formatRelative(org.creditTerminatesAt)}</span>
                  ) : (
                    <span className="text-fg-subtle">Not yet set</span>
                  )}
                </Td>
                <Td className="text-right">
                  <Link
                    to={`/organizations/${org.id}`}
                    className="text-sm text-accent hover:underline"
                  >
                    Grant credit
                  </Link>
                </Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      {/*
        What the month's money arrived as. The tiles above are all in the
        ledger's currency; the statements an operator reconciles them against
        are not -- BorderPay's is rupiah -- so this says how much came in
        each, and the month's effective rate beside the rupiah.
      */}
      {data.receivedByCurrency.length > 0 && (
        <Card>
          <CardHeader title="Received this month" description="Top-ups by the currency they were paid in, as each processor's statement shows them." />
          <ul className="divide-y divide-line">
            {data.receivedByCurrency.map((row) => (
              <li key={row.currency} className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1 px-5 py-3 text-sm">
                <span className="text-fg">
                  {row.chargedAmount !== null ? chargedMoney(row.chargedAmount, row.currency) : money(row.creditMicros, row.currency)}
                  <span className="ml-2 text-xs text-fg-subtle">
                    {row.count} {row.count === 1 ? "payment" : "payments"}
                  </span>
                </span>
                <span className="text-xs tabular-nums text-fg-subtle">
                  {row.chargedAmount !== null
                    ? `for ${money(row.creditMicros, c)} of credit${row.averageRate ? ` · avg ${chargedMoney(row.averageRate, row.currency)} per ${c}` : ""}`
                    : "the ledger's own currency"}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <SettlementQueueCard canWrite={canWrite} />

      <CurrenciesSummary />

      <Card>
        <CardHeader
          title="Recent payments"
          action={
            <Link to="/audit" className={buttonStyles("secondary", "sm")}>
              Audit log
            </Link>
          }
        />
        {data.recentPayments.length === 0 ? (
          <EmptyState
            icon="plans"
            title="No payments yet"
            description="Top-ups appear here as they are captured."
          />
        ) : (
          <Table
            head={
              <tr>
                <Th priority="sm">When</Th>
                <Th>Organization</Th>
                {/* Which processor. Two of them take money now, and a figure
                    here is reconciled against one statement or the other. */}
                <Th priority="sm">Via</Th>
                <Th numeric>Amount</Th>
                <Th>Status</Th>
              </tr>
            }
          >
            {data.recentPayments.map((payment) => (
              <tr key={payment.id} className="hover:bg-raised">
                <Td className="text-fg-subtle" priority="sm">
                  {formatRelative(payment.createdAt)}
                </Td>
                <Td>
                  <Link
                    to={`/organizations/${payment.organization.id}`}
                    className="text-accent hover:underline"
                  >
                    {payment.organization.name}
                  </Link>
                </Td>
                <Td className="text-fg-subtle" priority="sm">
                  {providerLabel(payment.provider)}
                </Td>
                <Td numeric>
                  {money(payment.amountMicros, payment.currency)}
                  {/* The charge where it differs from the credit: tax the
                      processor collected as seller. Beneath rather than
                      beside, so the column still sums to what the ledger
                      received. */}
                  {payment.totalMicros !== null && payment.totalMicros !== payment.amountMicros && (
                    <span className="block text-xs text-fg-subtle">
                      {money(payment.totalMicros, payment.currency)} charged
                      {payment.taxMicros ? ` · ${money(payment.taxMicros, payment.currency)} tax` : ""}
                    </span>
                  )}
                  {/* Or in another currency altogether: the rupiah BorderPay
                      took, which is the line on its statement. */}
                  {payment.charged && (
                    <span className="block text-xs text-fg-subtle">{chargedLabel(payment.charged)} charged</span>
                  )}
                </Td>
                <Td>
                  <PaymentTag payment={payment} />
                  {/* BorderPay cannot report a refund, so one made through its
                      dashboard is recorded here by hand -- the only way the
                      credit it bought comes back off the balance. */}
                  {canWrite && payment.provider === "borderpay" && payment.status === "completed" && payment.refundedMicros < payment.amountMicros && (
                    <button
                      type="button"
                      onClick={() => setRefunding(payment)}
                      className="ml-2 text-xs text-accent hover:text-accent-hover"
                    >
                      Record refund
                    </button>
                  )}
                </Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      {refunding && <RecordRefundModal payment={refunding} onClose={() => setRefunding(null)} />}

      <p className="flex items-start gap-2 text-xs leading-6 text-fg-subtle">
        <Icon name="warning" className="mt-1 size-3.5 shrink-0" />
        <span>
          From the credit ledger, current to the last hourly run. Not accounting records, and not
          the whole picture of what customers paid: sales tax charged by a merchant of record is
          collected and remitted by them, so it never reaches this ledger. Nothing here is
          reconciled against a processor's own statement.
        </span>
      </p>
    </>
  );
}

/**
 * A money tile.
 *
 * `note` exists because every figure on this page needs a sentence saying what
 * it is not — the whole risk of a prepaid dashboard is numbers that look like
 * revenue and are not.
 */
/**
 * A payment's outcome, refunds included.
 *
 * A refunded payment keeps `status: "completed"` — it did complete, money
 * arrived and a receipt was issued, and overwriting that would erase the only
 * record of it. Which meant this list showed a chargeback as indistinguishable
 * from revenue, and that is worse than showing nothing: an operator scanning
 * for the month's takings would count it.
 *
 * A partial refund reads "partly refunded" rather than a figure, because the
 * amount column beside it still shows what was taken and two numbers in one row
 * disagreeing is how a reader stops trusting either. The detail is on the
 * organization's own billing page.
 */
export function PaymentTag({
  payment,
}: {
  payment: { status: string; amountMicros: number; refundedMicros: number };
}) {
  if (payment.refundedMicros > 0) {
    const whole = payment.refundedMicros >= payment.amountMicros;
    return <Tag tone="bad">{whole ? "refunded" : "partly refunded"}</Tag>;
  }

  return (
    <Tag
      tone={
        payment.status === "completed" ? "good" : payment.status === "pending" ? "warn" : "neutral"
      }
    >
      {payment.status}
    </Tag>
  );
}

function Figure({
  label,
  value,
  detail,
  note,
  tone,
}: {
  label: string;
  value: string;
  detail: string;
  note: string;
  tone: "neutral" | "good";
}) {
  return (
    <div className="bg-surface px-5 py-4">
      <p className="text-sm font-medium text-fg-subtle">{label}</p>
      <p
        className={cx(
          "mt-2 text-2xl font-semibold tracking-tight tabular-nums",
          tone === "good" ? "text-mint" : undefined,
        )}
      >
        {value}
      </p>
      <p className="mt-1 text-xs text-fg-subtle">{detail}</p>
      <p className="mt-2 border-t border-line pt-2 text-xs text-fg-subtle">
        {note}
      </p>
    </div>
  );
}

/**
 * One line per presentment currency and the way to the page that operates
 * it. The rate is what a reader of this page wants to glance at beside the
 * rupiah received; setting it, and the history, live on /currencies.
 */
function CurrenciesSummary() {
  const { data } = useCurrencies();
  if (!data || data.rates.length === 0) return null;
  return (
    <Card>
      <CardHeader
        title="Currencies"
        description="What a dollar of credit is charged at in each currency a processor takes."
        action={
          <Link to="/currencies" className={buttonStyles("secondary", "sm")}>
            Manage
          </Link>
        }
      />
      <ul className="divide-y divide-line">
        {data.rates.map((rate) => (
          <li key={rate.currency} className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1 px-5 py-3 text-sm">
            <span className="text-fg">
              1 {data.base} = {chargedMoney(rate.rate, rate.currency)}
              <span className="ml-2 text-xs text-fg-subtle">
                {rate.mode === "auto" ? `market +${rate.marginPercent}%` : rate.source === "console" ? "set by hand" : "default"}
              </span>
            </span>
            <span className="text-xs tabular-nums text-fg-subtle">
              {rate.reference ? `market ${chargedMoney(rate.reference.rate, rate.currency)} (${rate.reference.asOf})` : "no reference yet"}
              {rate.syncError ? " · sync held" : ""}
            </span>
          </li>
        ))}
      </ul>
    </Card>
  );
}
